import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import { PackageOpen, AlertTriangle, CheckCircle2 } from 'lucide-react';
import {
  LENS_SPH_VALUES,
  LENS_CYL_VALUES,
  formatDecimalKey,
  formatCylLabel,
} from '../utils/lensGrid';

const Card = styled.div`
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  padding: 1.25rem 1.375rem;
  margin-top: 0.875rem;

  h3 {
    margin: 0 0 0.375rem;
    font-size: 0.9375rem;
    font-weight: 700;
    color: #0f172a;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
  p.sub {
    margin: 0 0 0.875rem;
    font-size: 0.8125rem;
    color: #64748b;
    line-height: 1.5;
  }
`;

const Line = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.625rem 0.75rem;
  border: 1px solid ${(p) => (p.$short ? '#fed7aa' : p.$over ? '#bfdbfe' : '#e2e8f0')};
  background: ${(p) => (p.$short ? '#fffbeb' : p.$over ? '#eff6ff' : '#fff')};
  border-radius: 12px;

  & + & { margin-top: 0.375rem; }

  .what { flex: 1; min-width: 0; }
  .name { font-weight: 700; color: #0f172a; font-size: 0.875rem; }
  .power { color: #64748b; font-size: 0.75rem; margin-top: 1px; }
  .expected {
    font-size: 0.75rem;
    color: #64748b;
    white-space: nowrap;
    font-variant-numeric: tabular-nums;
  }
  input {
    width: 68px;
    min-height: 44px;   /* counted on a tablet, with gloves on */
    text-align: center;
    font-size: 1rem;
    font-weight: 700;
    font-family: inherit;
    font-variant-numeric: tabular-nums;
    border: 1.5px solid #cbd5e1;
    border-radius: 10px;
    color: #0f172a;
  }
  input:focus { outline: none; border-color: #2563eb; box-shadow: 0 0 0 3px rgba(37,99,235,0.15); }
  .flag {
    font-size: 0.7rem;
    font-weight: 700;
    white-space: nowrap;
    color: ${(p) => (p.$short ? '#b45309' : '#1d4ed8')};
  }
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.875rem;
  flex-wrap: wrap;

  button {
    font-family: inherit;
    font-size: 0.875rem;
    font-weight: 600;
    border-radius: 10px;
    padding: 0.6rem 1.1rem;
    min-height: 44px;
    cursor: pointer;
  }
  .primary { background: #2563eb; border: 1px solid #2563eb; color: #fff; }
  .primary:disabled { opacity: 0.55; cursor: not-allowed; }
  .ghost { background: #fff; border: 1px solid #e2e8f0; color: #334155; }
  .note { font-size: 0.78rem; color: #64748b; }
`;

// The same SPH/CYL sheet the stock and order screens draw. A transfer of a few
// hundred powers is read as a grid, not as a list you scroll for a minute —
// staff already know where a power sits on this layout.
const GridWrap = styled.div`
  max-height: 62vh;
  overflow: auto;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  margin-bottom: 0.75rem;

  table { border-collapse: separate; border-spacing: 0; font-variant-numeric: tabular-nums; }
  th, td { border-right: 1px solid #eef2f7; border-bottom: 1px solid #eef2f7; padding: 0; }
  thead th {
    position: sticky; top: 0; z-index: 3;
    background: #f8fafc; color: #475569;
    font-size: 0.6875rem; font-weight: 700;
    height: 26px; min-width: 42px;
  }
  tbody th {
    position: sticky; left: 0; z-index: 2;
    background: #f1f5f9; color: #0f172a;
    font-size: 0.6875rem; font-weight: 700;
    width: 46px; min-width: 46px;
  }
  td { width: 42px; min-width: 42px; height: 30px; text-align: center; }
  td.empty { background: #fcfcfd; }

  input {
    width: 100%; height: 30px;
    border: none; outline: none; background: transparent;
    text-align: center; font-family: inherit;
    font-size: 0.75rem; font-weight: 700; color: #0f172a;
    font-variant-numeric: tabular-nums;
    -moz-appearance: textfield;
  }
  input::-webkit-outer-spin-button, input::-webkit-inner-spin-button {
    -webkit-appearance: none; margin: 0;
  }
  td.ok    input { background: #f0fdf4; color: #166534; }
  td.short input { background: #fffbeb; color: #b45309; }
  td.over  input { background: #eff6ff; color: #1d4ed8; }
  td.zero  input { background: #fef2f2; color: #991b1b; }
  input:focus { box-shadow: inset 0 0 0 2px #2563eb; }
`;

const Legend = styled.div`
  display: flex; flex-wrap: wrap; gap: 0.75rem;
  font-size: 0.7rem; color: #64748b; margin-bottom: 0.5rem;
  span { display: inline-flex; align-items: center; gap: 0.3rem; }
  i { width: 10px; height: 10px; border-radius: 3px; display: inline-block; }
`;

const Notice = styled.div`
  margin-top: 0.75rem;
  border-radius: 10px;
  padding: 0.6rem 0.8rem;
  font-size: 0.85rem;
  background: ${(p) => (p.$kind === 'error' ? '#fef2f2' : '#f0fdf4')};
  color: ${(p) => (p.$kind === 'error' ? '#991b1b' : '#166534')};
  border: 1px solid ${(p) => (p.$kind === 'error' ? '#fecaca' : '#bbf7d0')};
`;

/**
 * Count what is physically in a delivered box before it becomes stock.
 *
 * Counts default to what central packed, so a box that matches is one tap —
 * but the number is editable, and that edit is the only way a discrepancy ever
 * gets recorded. Scanning alone proves the box arrived, not what is in it.
 */
const ReceiveChecklist = ({ packageId, packageNumber, onDone }) => {
  const [lines, setLines] = useState(null);
  const [counts, setCounts] = useState({});
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);

  const token = () => localStorage.getItem('token');

  const load = useCallback(async () => {
    if (!packageId) return;
    try {
      const { data } = await axios.get(`/api/packages/${packageId}/manifest`, {
        headers: { Authorization: `Bearer ${token()}` },
      });
      const rows = data?.lines || [];
      setLines(rows);
      setCounts(Object.fromEntries(rows.map((l) => [l.requestId, String(l.expectedQty)])));
    } catch (err) {
      setNotice({ kind: 'error', message: err.response?.data?.message || 'Could not load the package contents.' });
      setLines([]);
    }
  }, [packageId]);

  useEffect(() => { load(); }, [load]);

  // This checklist renders in the scan card near the top of the page, but it is
  // also opened by a Count in button far below it in the package list. Without
  // this, pressing that button scrolled nothing and looked like a dead control
  // while the checklist sat off-screen above.
  const cardRef = useRef(null);
  useEffect(() => {
    if (lines?.length && cardRef.current) {
      cardRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [lines]);

  // Cells keyed the way the grid draws them, plus anything whose power is off
  // the printed sheet — those are listed under the grid rather than dropped, so
  // a lens in the box can never be invisible on the count.
  const { byCell, offGrid, activeSph, activeCyl } = useMemo(() => {
    const map = new Map();
    const off = [];
    const inSph = new Set(LENS_SPH_VALUES.map(formatDecimalKey));
    const inCyl = new Set(LENS_CYL_VALUES.map(formatDecimalKey));
    for (const line of lines || []) {
      if (line.sph == null || line.cyl == null) { off.push(line); continue; }
      const sk = formatDecimalKey(line.sph);
      const ck = formatDecimalKey(line.cyl);
      if (!inSph.has(sk) || !inCyl.has(ck)) { off.push(line); continue; }
      map.set(`${sk}:${ck}`, line);
    }
    // Only the rows and columns this package actually uses. Drawing the whole
    // 49x17 sheet for a handful of powers is a lot of empty scrolling.
    const usedSph = LENS_SPH_VALUES.filter((s) => LENS_CYL_VALUES
      .some((c) => map.has(`${formatDecimalKey(s)}:${formatDecimalKey(c)}`)));
    const usedCyl = LENS_CYL_VALUES.filter((c) => LENS_SPH_VALUES
      .some((s) => map.has(`${formatDecimalKey(s)}:${formatDecimalKey(c)}`)));
    return { byCell: map, offGrid: off, activeSph: usedSph, activeCyl: usedCyl };
  }, [lines]);

  // A clean delivery is the common case; this is the one press that says so.
  const acceptAllAsPacked = () => {
    setCounts(Object.fromEntries((lines || []).map((l) => [l.requestId, String(l.expectedQty)])));
  };

  const confirm = async () => {
    setBusy(true);
    setNotice(null);
    try {
      const { data } = await axios.post(
        `/api/packages/${packageId}/confirm-receipt`,
        { counts },
        { headers: { Authorization: `Bearer ${token()}` } }
      );
      const bits = [
        `${data.credited} unit(s) added to store stock`,
        data.short?.length ? `${data.short.length} line(s) short — sent back to central` : null,
        data.over?.length ? `${data.over.length} line(s) over — central notified` : null,
      ].filter(Boolean);
      setNotice({ kind: 'success', message: bits.join(' · ') });
      setLines([]);
      if (typeof onDone === 'function') onDone(data);
    } catch (err) {
      setNotice({ kind: 'error', message: err.response?.data?.message || 'Could not confirm the receipt.' });
    } finally {
      setBusy(false);
    }
  };

  if (!packageId) return null;
  if (lines === null) return null;

  // Say something either way. Rendering null on an empty manifest meant the
  // Count in button looked broken when in fact there was simply nothing left
  // outstanding on that package.
  if (!lines.length) {
    return (
      <Card ref={cardRef}>
        {notice
          ? <Notice $kind={notice.kind}>{notice.message}</Notice>
          : <p className="sub">Nothing left to count in {packageNumber || 'this package'} — every line has already been received.</p>}
      </Card>
    );
  }

  const totalExpected = lines.reduce((s, l) => s + l.expectedQty, 0);
  const totalCounted = lines.reduce((s, l) => s + (parseInt(counts[l.requestId], 10) || 0), 0);
  const anyShort = lines.some((l) => (parseInt(counts[l.requestId], 10) || 0) < l.expectedQty);
  const productName = lines[0]?.productName || 'Lens';

  return (
    <Card ref={cardRef}>
      <h3>
        <PackageOpen size={16} color="#2563eb" /> Count what’s in {packageNumber || 'the box'}
        {' '}— {productName} ({lines.length} correction{lines.length === 1 ? '' : 's'}, {totalExpected} unit{totalExpected === 1 ? '' : 's'})
      </h3>
      <p className="sub">
        Nothing has been added to your stock yet. Every cell already holds what central packed, so a
        box that matches is one press of Confirm — change only the cells that came up wrong. Blank
        cells are not in this package.
      </p>

      <Legend>
        <span><i style={{ background: '#f0fdf4', border: '1px solid #bbf7d0' }} /> matches what was packed</span>
        <span><i style={{ background: '#fffbeb', border: '1px solid #fed7aa' }} /> short — goes back to central</span>
        <span><i style={{ background: '#eff6ff', border: '1px solid #bfdbfe' }} /> more than packed</span>
        <span><i style={{ background: '#fef2f2', border: '1px solid #fecaca' }} /> none arrived</span>
      </Legend>

      {offGrid.length > 0 && (
        <p className="sub" style={{ color: '#b45309' }}>
          {offGrid.length} line(s) sit outside the printed sheet and are listed under the grid.
        </p>
      )}

      <GridWrap>
        <table>
          <thead>
            <tr>
              <th style={{ position: 'sticky', left: 0, zIndex: 4 }}>SPH</th>
              {activeCyl.map((cyl) => <th key={cyl}>{formatCylLabel(cyl)}</th>)}
            </tr>
          </thead>
          <tbody>
            {activeSph.map((sph) => (
              <tr key={sph}>
                <th>{formatDecimalKey(sph)}</th>
                {activeCyl.map((cyl) => {
                  const line = byCell.get(`${formatDecimalKey(sph)}:${formatDecimalKey(cyl)}`);
                  if (!line) return <td key={cyl} className="empty" />;
                  const counted = parseInt(counts[line.requestId], 10);
                  const tone = !Number.isFinite(counted) ? 'ok'
                    : counted === 0 ? 'zero'
                      : counted < line.expectedQty ? 'short'
                        : counted > line.expectedQty ? 'over' : 'ok';
                  return (
                    <td key={cyl} className={tone}>
                      <input
                        type="number"
                        min="0"
                        inputMode="numeric"
                        title={`${line.displayCorrection} — central packed ${line.expectedQty}`}
                        aria-label={`Counted quantity for ${line.displayCorrection}, packed ${line.expectedQty}`}
                        value={counts[line.requestId] ?? ''}
                        onChange={(e) => setCounts((prev) => ({ ...prev, [line.requestId]: e.target.value }))}
                      />
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </GridWrap>

      {offGrid.length > 0 && (
        <div style={{ marginBottom: '0.75rem' }}>
          {offGrid.map((l) => {
            const counted = parseInt(counts[l.requestId], 10);
            const isShort = Number.isFinite(counted) && counted < l.expectedQty;
            const isOver = Number.isFinite(counted) && counted > l.expectedQty;
            return (
              <Line key={l.requestId} $short={isShort} $over={isOver}>
                <div className="what">
                  <div className="name">{l.productName}</div>
                  <div className="power">{l.displayCorrection || 'No correction recorded'}</div>
                </div>
                <div className="expected">packed {l.expectedQty}</div>
                <input
                  type="number"
                  min="0"
                  inputMode="numeric"
                  aria-label={`Counted quantity for ${l.displayCorrection}, packed ${l.expectedQty}`}
                  value={counts[l.requestId] ?? ''}
                  onChange={(e) => setCounts((prev) => ({ ...prev, [l.requestId]: e.target.value }))}
                />
              </Line>
            );
          })}
        </div>
      )}

      <Actions>
        <button type="button" className="primary" onClick={confirm} disabled={busy}>
          {busy ? 'Confirming…' : `Confirm receipt (${totalCounted} unit${totalCounted === 1 ? '' : 's'})`}
        </button>
        <button type="button" className="ghost" onClick={acceptAllAsPacked} disabled={busy}>
          All arrived as packed
        </button>
        <button type="button" className="ghost" onClick={load} disabled={busy}>
          Reset counts
        </button>
        <span className="note">
          {anyShort
            ? <><AlertTriangle size={12} style={{ verticalAlign: '-2px' }} /> {totalCounted} of {totalExpected} — short lines go back to central</>
            : <><CheckCircle2 size={12} style={{ verticalAlign: '-2px' }} /> {totalCounted} of {totalExpected} counted</>}
        </span>
      </Actions>

      {notice && <Notice $kind={notice.kind}>{notice.message}</Notice>}
    </Card>
  );
};

export default ReceiveChecklist;
